/**
 * Componentes visuais compartilhados pelas páginas do módulo Administração :: Configurações.
 *
 * O que também é usado por outros módulos mora em `../lifeshub-common`.
 */
import React from 'react'

import { styled } from '@mui/material/styles'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import Alert from '@mui/material/Alert'

import { bannerSx, LifesHubBannerTone } from '../lifeshub-tokens'

/** Coluna de cards/painéis empilhados dentro de uma aba. */
export const SettingsStack = styled('div')({
    display: 'grid',
    gap: 10,
    minWidth: 0,
    alignContent: 'start'
})

/** Cabeçalho de card das configurações (título `h3` + `.subtitle`). */
export const CardHead = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 20,
    padding: '16px 18px 12px',
    flexShrink: 0,
    '& h3': { margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.2 },
    '& .subtitle': { margin: '5px 0 0', color: 'var(--muted)', fontSize: 12.5 },
    '@media (max-width: 720px)': {
        flexDirection: 'column'
    }
})

/** Rótulo de campo que não está associado a um `input` (usa `div` em vez de `label`). */
export const FieldCaption = styled('div')({
    fontSize: 12,
    fontWeight: 700,
    color: '#303733'
})

/** Grade que distribui campos em colunas conforme a largura disponível. */
export const FieldGrid = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 14,
    minWidth: 0
})

/** Bloco destacado que agrupa um conjunto de ajustes. */
export const SettingsPanel = styled('div')({
    padding: 16,
    border: '1px solid var(--border)',
    borderRadius: 11,
    backgroundColor: '#fbfcfb',
    minWidth: 0
})

export type BannerProps = {
    tone?: LifesHubBannerTone
    children: React.ReactNode
}

export function Banner({ tone = 'blue', children }: BannerProps) {
    const icon =
        tone === 'warning' || tone === 'danger' ? (
            <WarningAmberIcon sx={{ fontSize: 16 }} />
        ) : (
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        )

    return (
        <Alert variant="outlined" icon={icon} sx={bannerSx(tone)}>
            {children}
        </Alert>
    )
}
